import {
  useTemplateSelector,
  type AppliedTemplate,
} from '@/components/templates/use-template-selector';
import type { InvitationMethod } from '@/types/invitation';
import type { TemplateLibraryLanguage, TemplatePair } from '@fluenceur/types';
import { useMemo } from 'react';

import { methodToPair } from './send-invitation-templates';

export type { AppliedTemplate };

interface UseInvitationTemplateSelectorArgs {
  brandId: string | undefined;
  /**
   * Send mode whose (channel, context) pair backs the template library. Optional
   * so scopes with no {@link InvitationMethod} (the sponsored collab proposal
   * email lives on the EMAIL/COLLAB pair) can pass `pair` directly instead.
   */
  method?: InvitationMethod;
  /** Explicit (channel, context) override; takes precedence over `method`. */
  pair?: TemplatePair | null;
  /** Current send language. Templates are scoped to it (owned by the language toggle). */
  language: TemplateLibraryLanguage;
  onTemplateApplied: (applied: AppliedTemplate) => void | boolean | Promise<void | boolean>;
  autoSelectDefault: boolean;
  initialSelectedEntryId?: string | null;
}

/**
 * Invitation-flow adapter over the generic {@link useTemplateSelector}: resolves
 * the (channel, context) pair from an explicit `pair` or the active
 * {@link InvitationMethod} and forwards everything else unchanged. Kept as a
 * named hook so the invitation call sites (and their tests) stay expressed in
 * send-mode terms, while the sponsored-collab module reuses it via `pair`.
 */
export const useInvitationTemplateSelector = ({
  brandId,
  method,
  pair,
  language,
  onTemplateApplied,
  autoSelectDefault,
  initialSelectedEntryId,
}: UseInvitationTemplateSelectorArgs) => {
  const resolvedPair = useMemo(
    () => pair ?? (method ? methodToPair(method) : null),
    [pair, method]
  );

  return useTemplateSelector({
    brandId,
    pair: resolvedPair,
    language,
    onTemplateApplied,
    autoSelectDefault,
    initialSelectedEntryId,
  });
};
